import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";

export const G = "#2CF6B3", R = "#FF3D68", A = "#F59E0B", C = "#00F0FF", B = "#38BDF8";
export const usd = (v) => (v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
export const k$ = (v) => (v == null ? "—" : Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(1)}K` : `$${Math.round(v)}`);
export const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`);
export const pTone = (v) => (v == null ? undefined : v >= 0 ? G : R);
const DIR = { up: ["▲", G], down: ["▼", R], flat: ["▬", A] };
const SP = ["lamb", "goat", "veal", "beef"];

export const useHtu = () => {
  const [d, setD] = useState({});
  useEffect(() => {
    let on = true;
    const load = () => Promise.all([api.get("/htu/summary").then((r) => r.data).catch(() => null), api.get("/htu/outlook").then((r) => r.data).catch(() => null)])
      .then(([htu, outlook]) => on && setD({ htu, outlook }));
    load(); const t = setInterval(load, 600000); return () => { on = false; clearInterval(t); };
  }, []);
  return d;
};

export const Panel = ({ title, code, children, testid, className = "" }) => (
  <section className={`fc-panel min-h-0 p-[0.6em] flex flex-col overflow-hidden ${className}`} data-testid={testid}>
    <p className="fc-label mb-[0.35em] flex justify-between gap-[1em]"><span className="truncate">{title}</span>{code && <span className="text-cyan-200/50 shrink-0">{code}</span>}</p>
    <div className="min-h-0 flex-1">{children}</div>
  </section>
);

export const Num = ({ l, v, tone, tag, sub, big, testid }) => (
  <div className="rounded-[0.5em] border border-cyan-300/15 bg-cyan-300/[0.03] px-[0.55em] py-[0.3em] min-w-0" data-testid={testid}>
    <p className="fc-label truncate">{l}{tag && <span className="ml-[0.5em] text-amber-300">{tag}</span>}</p>
    <p className={`nx-orbitron font-black truncate ${big ? "text-[2.1em]" : "text-[1.45em]"}`} style={{ color: tone || "#E6F6FF" }}>{v}</p>
    {sub && <p className="fc-num text-[0.85em] text-cyan-200/60 truncate">{sub}</p>}
  </div>
);

export const Bars = ({ data, col = G, h = "100%", showV = true, testid }) => {
  const max = Math.max(1, ...data.map((x) => x.v || 0));
  return (
    <div className="flex items-end gap-[0.35em] min-h-0" style={{ height: h }} data-testid={testid}>
      {data.map((x, i) => (
        <div key={x.k || i} className="flex-1 h-full flex flex-col justify-end items-center min-w-0">
          {showV && <span className="fc-num text-[0.75em] text-cyan-100/80 whitespace-nowrap">{x.top ?? k$(x.v)}</span>}
          <div className="w-full rounded-t-[0.3em] transition-[height] duration-700"
            style={{ height: `${Math.max(2, ((x.v || 0) / max) * 82)}%`, background: x.v ? `linear-gradient(180deg, ${x.c || col}, ${x.c || col}22)` : "#ffffff14",
              boxShadow: x.hi ? `0 0 14px ${x.c || col}` : "none", outline: x.partial ? `1px dashed ${A}` : "none" }} />
          <span className="fc-num text-[0.72em] text-cyan-300/60 mt-[0.15em] whitespace-nowrap">{x.l}</span>
        </div>
      ))}
    </div>
  );
};

const Head = ({ s, tag }) => (
  <div className="flex items-baseline gap-[1em] min-w-0">
    <p className="nx-orbitron text-[2em] font-black tracking-wider text-white whitespace-nowrap" style={{ textShadow: `0 0 14px ${G}66` }}>Halal Transactions of USA</p>
    <p className="fc-label text-[1em] text-emerald-300/80 whitespace-nowrap">Wholesale Halal Meat · {tag}</p>
    {s.alert && <p className="ml-auto text-[1em] text-amber-200 truncate" data-testid="htu-alert">{s.alert}</p>}
  </div>
);

const Sync = () => <div className="fc-panel p-[1em] fc-label" data-testid="tv-scene-htu">SYNCING HALAL TRANSACTIONS OF USA…</div>;

/* ---------------- shared ---------------- */
const Outlook = ({ o }) => (
  <Panel title="LIVESTOCK OUTLOOK · NEXT 4 WEEKS · USA" code={o?.date ? `AI · ${o.date}` : "6:00 AM ET"} testid="htu-outlook">
    <div className="grid grid-cols-2 gap-[0.4em]">
      {SP.map((sp) => {
        const x = (o?.species || []).find((y) => y.species === sp) || {};
        const [ic, col] = DIR[x.outlook] || ["—", "#94a3b8"];
        return (
          <div key={sp} className="rounded-[0.5em] border border-cyan-300/15 px-[0.5em] py-[0.3em] min-w-0" data-testid={`htu-outlook-${sp}`}>
            <p className="flex items-baseline gap-[0.5em]"><span className="fc-label">{sp.toUpperCase()}</span>
              <span className="nx-orbitron text-[1.25em] font-black" style={{ color: col }}>{ic} {(x.outlook || "—").toUpperCase()}</span>
              <span className="fc-label ml-auto">{(x.confidence || "").toUpperCase()}</span></p>
            <p className="text-[0.95em] text-amber-200 font-bold truncate">{x.action || "—"}</p>
            <p className="text-[0.82em] text-cyan-100/65 leading-tight line-clamp-2">{x.why || ""}</p>
          </div>
        );
      })}
    </div>
  </Panel>
);

const News = ({ o }) => {
  const items = o?.midwest_news || [];
  return (
    <Panel title="CATTLE & LIVESTOCK MARKET NEWS · USA / MIDWEST" code={`${items.filter((h) => h.midwest).length} MIDWEST · ${items.length} TOTAL`} testid="htu-news">
      <div className="grid grid-cols-1">
        {items.map((h, i) => (
          <p key={i} className="text-[0.95em] text-cyan-50 leading-snug truncate border-b border-white/5 py-[0.18em]" data-testid={`htu-headline-${i}`}>
            {h.midwest && <span className="text-amber-300 font-bold mr-[0.4em]">MIDWEST</span>}▸ {h.title} <span className="text-cyan-300/60">— {h.source}</span>
          </p>
        ))}
      </div>
    </Panel>
  );
};

const INS = { trend: ["TREND", B], risk: ["RISK", R], opportunity: ["OPPORTUNITY", G], action: ["ACTION", A] };
const Insights = ({ o }) => (
  <Panel title="AI BUSINESS INSIGHTS · FROM HTU SALES + MARKET OUTLOOK" code={o?.date ? `GEMINI · ${o.date}` : ""} testid="htu-insights">
    <div className="grid grid-cols-2 gap-[0.45em]">
      {(o?.insights || []).map((x, i) => {
        const [lab, col] = INS[x.type] || ["NOTE", C];
        return (
          <div key={i} className="rounded-[0.5em] border px-[0.6em] py-[0.4em] min-w-0" style={{ borderColor: `${col}55`, background: `${col}0d` }} data-testid={`htu-insight-${i}`}>
            <p className="flex gap-[0.6em] items-baseline"><span className="fc-label" style={{ color: col }}>{lab}</span>
              <span className="text-[1.1em] font-black text-white truncate">{x.title}</span></p>
            <p className="text-[0.95em] text-cyan-50/85 leading-snug line-clamp-2">{x.detail}</p>
          </div>
        );
      })}
    </div>
  </Panel>
);

/* ---------------- slide 1: performance (numbers + compact graphics) ---------------- */
const lbs = (v) => (v == null ? "—" : `${Math.round(v).toLocaleString()} lb`);
const pc = (v, d = 1) => (v == null ? "—" : `${v.toFixed(d)}%`);

const PnL = ({ s }) => {
  const rg = s.app?.ranges || {};
  const cols = [[s.last_day.label.toUpperCase(), "day"], ["WEEK TO DATE", "wtd"], ["MONTH TO DATE", "mtd"], ["YEAR TO DATE", "ytd"]];
  return (
    <Panel title="REVENUE & PROFIT AFTER WAGES" code="HTU APP · REVENUE − COGS − DEAD LOSS − WAGES" testid="htu-pnl">
      <div className="grid grid-cols-4 gap-[0.35em]">
        {cols.map(([l, k]) => {
          const r = rg[k] || {};
          const p = s.pnl[k];
          return (
            <div key={k} className="rounded-[0.5em] border border-cyan-300/15 bg-cyan-300/[0.03] px-[0.6em] py-[0.3em] min-w-0" data-testid={`htu-pnl-${k}`}>
              <p className="fc-label truncate">{l}</p>
              <div className="flex items-baseline justify-between gap-[0.5em]">
                <p className="nx-orbitron text-[1.6em] font-black truncate text-[#E6F6FF]">{k === "day" ? s.last_day.display : usd(s.revenue[k])}</p>
                <p className="nx-orbitron text-[1.35em] font-black whitespace-nowrap" style={{ color: pTone(p) || "#94a3b8" }}>{usd(p)}</p>
              </div>
              <p className="fc-num text-[0.82em] text-cyan-200/65 truncate">COGS {k$(r.cogs)} · wages {k$(r.wages)} · GM {pc(r.gross_margin_pct)} · net {pc(r.margin_after_wages_pct)}</p>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

const Ops = ({ s }) => {
  const o = s.app?.ops || {};
  return (
    <div className="grid grid-cols-8 gap-[0.35em]" data-testid="htu-ops">
      <Num l="L&G RECOVERY" v={pc(o.lg_recovery_pct)} sub="Lamb & Goat carcass yield" tone={G} testid="htu-ops-lg-rec" />
      <Num l="V&B RECOVERY" v={pc(o.vb_recovery_pct)} sub="Veal & Beef carcass yield" tone={B} testid="htu-ops-vb-rec" />
      <Num l="GROSS MARGIN" v={pc(o.gross_margin_pct)} sub={`after wages ${pc(o.margin_after_wages_all)}`} testid="htu-ops-gm" />
      <Num l="LABOR $/LB YTD" v={o.labor_per_lb_ytd == null ? "—" : `$${o.labor_per_lb_ytd.toFixed(2)}`} sub={`4-wk $${(o.labor_per_lb_4wk ?? 0).toFixed(2)} · ${o.labor_weeks ?? "—"} wks`} testid="htu-ops-labor" />
      <Num l="PROFIT AFTER WAGES" v={usd(o.profit_after_wages_all)} sub={`all time · wages ${k$(o.wages_all)}`} tone={pTone(o.profit_after_wages_all)} testid="htu-ops-paw" />
      <Num l="LIVE WEIGHT BOUGHT" v={lbs(o.live_lbs_all)} sub="all time" testid="htu-ops-live" />
      <Num l="CARCASS SOLD" v={lbs(o.carcass_lbs_all)} sub="all time" testid="htu-ops-carcass" />
      <Num l="L&G SELL / BUY LB" v={o.lg ? `$${o.lg.sell_per_lb.toFixed(2)}` : "—"} sub={o.lg ? `buy $${o.lg.buy_per_lb.toFixed(2)} · V&B $${o.vb.sell_per_lb.toFixed(2)}/$${o.vb.buy_per_lb.toFixed(2)}` : ""} testid="htu-ops-perlb" />
    </div>
  );
};

const Stock = ({ s }) => {
  const st = s.app?.stock;
  if (!st) return null;
  return (
    <Panel title="INVENTORY ON HAND · HEAD" code="DAYS AT YESTERDAY'S PACE" testid="htu-inventory">
      <div className="grid grid-cols-2 gap-[0.35em]">
        {[["lamb_goat", "LAMB & GOAT"], ["veal_beef", "VEAL & BEEF"]].map(([k, l]) => {
          const x = st[k] || {};
          const low = x.days_of_stock != null && x.days_of_stock < 2;
          return <Num key={k} l={l} v={x.head == null ? "—" : `${Math.round(x.head)} head`} big tone={low ? R : undefined}
            sub={`${usd(x.cost_on_hand)} cost · sold ${x.sold_yday ?? 0} yday · ${x.days_of_stock ?? "—"} days`} testid={`htu-inv-${k}`} />;
        })}
      </div>
    </Panel>
  );
};

const Customers = ({ s }) => {
  const c = s.app?.top_customers;
  if (!c?.length) return null;
  const max = c[0].revenue || 1;
  return (
    <Panel title="TOP 5 CUSTOMERS · REVENUE" code="SINCE FIRST SALE" testid="htu-top-customers">
      {c.map((x) => (
        <div key={x.name} className="mb-[0.2em]" data-testid={`htu-cust-${x.name.slice(0, 12).replace(/\W+/g, "-")}`}>
          <p className="flex justify-between text-[0.95em]"><span className="text-cyan-50 truncate">{x.name}</span><span className="fc-num text-cyan-100 shrink-0 ml-[0.5em]">{usd(x.revenue)} · ${x.avg_per_lb?.toFixed(2)}/lb</span></p>
          <div className="h-[0.35em] rounded-full bg-white/10"><div className="h-full rounded-full" style={{ width: `${(x.revenue / max) * 100}%`, background: G }} /></div>
        </div>
      ))}
    </Panel>
  );
};

const Pace = ({ s }) => {
  const t = s.stats || {};
  const dowMax = Math.max(...(t.dow_avg || []).map((x) => x.v), 0);
  const dow = (t.dow_avg || []).map((x) => ({ k: x.d, l: x.d, v: x.v, hi: x.v === dowMax, c: C }));
  return <Panel title="AVG REVENUE BY WEEKDAY" code="BEST DAY GLOWS" testid="htu-chart-dow"><Bars data={dow} col={C} testid="htu-bars-dow" /></Panel>;
};

export const HtuNumbers = ({ d }) => {
  const s = d.htu;
  if (!s) return <Sync />;
  const t = s.stats || {};
  const daily = (t.daily14 || []).map((x) => ({ k: x.date, l: x.label.split(" ")[0][0] + x.label.split(" ").pop(), v: x.v, hi: x.date === s.last_day.date, c: x.v ? G : R }));
  const weeks = (t.weekly8 || []).map((w) => ({ k: w.week, l: w.week.replace(/^\d{4}-/, ""), v: w.v, partial: w.partial, c: w.partial ? A : B, hi: t.best_week?.week === w.week,
    top: w.wow != null ? `${w.wow > 0 ? "▲" : "▼"}${Math.abs(w.wow).toFixed(0)}%` : w.partial ? "wtd" : k$(w.v) }));
  return (
    <div className="h-full min-h-0 grid gap-[0.45em]" style={{ gridTemplateRows: "auto auto auto minmax(0,1fr) minmax(0,0.85fr)" }} data-testid="tv-scene-htu">
      <Head s={s} tag="PERFORMANCE" />
      <PnL s={s} />
      <Ops s={s} />
      <div className="min-h-0 grid gap-[0.45em]" style={{ gridTemplateColumns: "1fr 1.1fr 1fr" }}>
        <Stock s={s} />
        <Customers s={s} />
        <Panel title="THIS WEEK" code={`WTD ${pct(t.wtd_vs_lw_pct)} VS LAST WK`} testid="htu-week">
          <div className="grid grid-cols-2 gap-[0.35em]">
            <Num l="TODAY LIVE" v={usd(t.today_sales)} sub={`${s.orders.today ?? 0} orders`} testid="htu-today-live" />
            <Num l={`LAST WEEK ${t.last_full_week_label?.replace(/^\d{4}-/, "") || ""}`} v={usd(t.last_full_week)} sub={`WoW ${pct(t.wow_pct)}`} tone={pTone(t.wow_pct)} testid="htu-k-lfw" />
            <Num l="VS 4-WEEK AVG" v={pct(t.vs_avg4w_pct)} sub={`4w avg ${usd(t.avg4w)}`} tone={pTone(t.vs_avg4w_pct)} testid="htu-k-avg4" />
            <Num l="BEST DAY 14D" v={usd(t.best_day?.v)} sub={t.best_day?.label} tone={G} testid="htu-k-best" />
          </div>
        </Panel>
      </div>
      <div className="min-h-0 grid gap-[0.45em]" style={{ gridTemplateColumns: "1.5fr 1fr" }}>
        <Panel title="DAILY REVENUE · 14 DAYS" code={`AVG ${usd(t.d14_avg)} · TOTAL ${usd(t.d14_total)}`} testid="htu-chart-daily"><Bars data={daily} testid="htu-bars-daily" /></Panel>
        <Panel title="WEEKLY · 8 WEEKS · WoW" code={`8-WK ${usd(t.w8_total)}`} testid="htu-chart-weekly"><Bars data={weeks} col={B} testid="htu-bars-weekly" /></Panel>
      </div>
    </div>
  );
};

/* ---------------- slide 2: AI insights + outlook + news ---------------- */
export const HtuProgress = ({ d }) => {
  const s = d.htu;
  if (!s) return <Sync />;
  return (
    <div className="h-full min-h-0 grid gap-[0.45em]" style={{ gridTemplateRows: "auto auto auto minmax(0,1fr)" }} data-testid="tv-scene-htu-progress">
      <Head s={s} tag="INSIGHTS" />
      <Insights o={d.outlook} />
      <Outlook o={d.outlook} />
      <div className="min-h-0 grid gap-[0.45em]" style={{ gridTemplateColumns: "1.7fr 1fr" }}>
        <News o={d.outlook} />
        <Pace s={s} />
      </div>
    </div>
  );
};
